import { useMemo, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

interface Props {
  /** SetupIntent client_secret from POST /subscriptions/{id}/card-setup. */
  clientSecret: string;
  /** Connected account the SetupIntent was created on (Stripe Connect). */
  stripeAccountId: string;
  onDone: () => void;
  onCancel: () => void;
}

// CardSetupForm collects and saves a card for off-session subscription charges,
// using the SetupIntent client_secret. Stripe.js is loaded against the club's
// connected account (Connect). The saved payment method is stored on the
// subscription server-side by the setup_intent.succeeded webhook.
export function CardSetupForm({ clientSecret, stripeAccountId, onDone, onCancel }: Props) {
  const { t } = useTranslation('common');
  const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
    if (!publishableKey) return null;
    return loadStripe(
      publishableKey,
      stripeAccountId ? { stripeAccount: stripeAccountId } : undefined,
    );
  }, [stripeAccountId]);

  if (!stripePromise) {
    return <p className="text-sm text-destructive">{t('notices.card.unavailable')}</p>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CardSetupInner onDone={onDone} onCancel={onCancel} />
    </Elements>
  );
}

function CardSetupInner({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('common');
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    setBusy(false);
    if (result.error) {
      setError(result.error.message ?? t('notices.card.failed'));
      return;
    }
    onDone();
  };

  return (
    <div className="flex flex-col gap-3">
      <PaymentElement />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button onClick={() => void submit()} disabled={busy || !stripe}>
          {busy ? t('notices.card.saving') : t('notices.card.save')}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {t('notices.card.cancel')}
        </Button>
      </div>
    </div>
  );
}
